import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useRecoilValue } from 'recoil';
import { Blog } from '../../interfaces/Blog/Blog';
import { getBlog } from '../../api/Blog';
import { bodyWidth } from '../../utils/Style';
import { BreadcrumbElement } from '../../interfaces/Breadcrumb/BreadcrumbElement';
import { settingsAtom } from '../../atoms/Settings';
import { GenericHelmet } from '../../components/Helmet/GenericHelmet';
import { GenericBreadcrumbs } from '../../components/Breadcrumbs/GenericBreadcrumbs';
import { useLocalizedText } from '../../hooks/useLocalizedText';

export const BlogPage = () => {
  const t = useLocalizedText();

  const { slug } = useParams<string>();

  const settings = useRecoilValue(settingsAtom);

  const [blog, setBlog] = useState<Blog | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    getBlog(slug ?? '')
      .then(setBlog)
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [slug]);

  const blogsTitle = settings.settingsV1.blog_text || t('blog.title');

  const breadcrumbElements: BreadcrumbElement[] = [
    {
      name: blogsTitle,
      url: 'blog',
    },
    {
      name: blog?.title || '',
      url: `blog/${slug}`,
    },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        alignItems: 'center',
      }}
    >
      <GenericHelmet
        title={blog?.title || blogsTitle}
        meta={{
          ...blog?.meta,
          title: blog?.meta.title || blog?.title || blogsTitle,
          canonicalURL: blog?.meta.canonicalURL || `blog/${slug}`,
        }}
        breadcrumb={breadcrumbElements}
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: bodyWidth,
          py: 3,
          px: 1.5,
          gap: 1.5,
          boxSizing: 'border-box',
        }}
      >
        <GenericBreadcrumbs breadcrumbElements={breadcrumbElements} />

        {loading && (
          <CircularProgress color='primary' sx={{ alignSelf: 'center' }} />
        )}
        {!loading && !blog && <Typography>{t('blog.not_found')}</Typography>}
        {!!blog?.title && (
          <Typography sx={{ fontSize: '32px', fontWeight: 600 }}>
            {blog.title}
          </Typography>
        )}

        {!!blog?.description && (
          <Box
            dangerouslySetInnerHTML={{ __html: blog.description }}
            sx={{
              '& figure.image': {
                display: 'table',
                margin: '0.9em auto',
                maxWidth: '100%',
                textAlign: 'center',
              },
              '& figure.image img': {
                display: 'block',
                margin: '0 auto',
                maxWidth: '100%',
                height: 'auto',
              },
            }}
          />
        )}
      </Box>
    </Box>
  );
};
